import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import './LandingPage.css';

const VALUE_PROPS = [
  {
    icon: '⚙',
    title: 'Water-Powered & Historic',
    body: 'Our flour is stone-ground on 1840s millstones at Woodson\'s Mill, built in 1794 — Virginia\'s only remaining commercial water-powered mill.',
  },
  {
    icon: '✦',
    title: 'Heritage Grains, Small Farms',
    body: 'We source heritage and heirloom varieties from small family farms across Appalachia and the Mid-Atlantic, paying 3–4× commodity prices to support regenerative agriculture.',
  },
  {
    icon: '↗',
    title: 'Milled Fresh to Order',
    body: 'Every batch is stone-ground during our monthly milling sessions and delivered fresh. Place orders through your wholesale portal and track fulfillment in real time.',
  },
];

const SHIPPING_ITEMS = [
  {
    label: 'Delivery Options',
    detail: 'We offer regular deliveries to the New River Valley, Roanoke Valley, Shenandoah Valley, Piedmont Triad, Lynchburg, Charlottesville, Richmond, Fredericksburg, and DC Area, plus select locations in southwest Virginia, western North Carolina, and eastern and central Tennessee. Delivery fee varies by location. Minimum order for delivery is $200. Orders below the minimum may be shipped instead.',
  },
  {
    label: 'Shipping Options',
    detail: 'For those outside our delivery area or placing smaller orders, products can be shipped via UPS. Freight available for orders of 500 lbs and up. Bulk orders are more economical in 50 lb quantities.',
  },
  {
    label: 'Pickup Options',
    detail: 'Contact us to arrange a pickup date at Woodson\'s Mill in Nelson County, VA.',
  },
];

const QuoteCard = ({ t }) => (
  <blockquote className="landing__quote-card">
    {t.has_image && (
      <div className="landing__quote-image-wrap">
        <img
          src={t.image_url}
          alt={t.name}
          className="landing__quote-image"
          loading="lazy"
        />
      </div>
    )}
    <span className="landing__quote-mark">"</span>
    <p className="landing__quote-text">{t.quote}</p>
    <footer className="landing__quote-footer">
      <span className="landing__quote-dash">—</span>
      <cite className="landing__quote-name">{t.name}</cite>
      <span className="landing__quote-business">{t.business_name}</span>
    </footer>
  </blockquote>
);

const LandingPage = () => {
  const [sessions, setSessions] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSlide, setActiveSlide] = useState(0);
  const [perPage, setPerPage] = useState(
    typeof window !== 'undefined' && window.innerWidth > 768 ? 2 : 1
  );
  const [transitionEnabled, setTransitionEnabled] = useState(true);
  const autoplayRef = useRef(null);
  const lockRef = useRef(false);       // prevent interaction during snap-back
  const trackRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [sessionsRes, testimonialsRes] = await Promise.all([
          api.get('/public/sessions/'),
          api.get('/public/testimonials/'),
        ]);
        setSessions(Array.isArray(sessionsRes.data) ? sessionsRes.data : (sessionsRes.data.results || []));
        setTestimonials(Array.isArray(testimonialsRes.data) ? testimonialsRes.data : (testimonialsRes.data.results || []));
      } catch {
        // Fail silently — page still renders with static content
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Track viewport for responsive cards-per-page (debounced)
  useEffect(() => {
    let timer;
    const onResize = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        const next = window.innerWidth > 768 ? 2 : 1;
        setPerPage((prev) => {
          if (prev !== next) setActiveSlide(0);
          return next;
        });
      }, 150);
    };
    window.addEventListener('resize', onResize);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  // ── Infinite-loop carousel ──────────────────────────────────
  // Real cards: indices 0 … cardCount-1
  // Clone zone: we append `perPage` clones of the first cards at the end.
  // When activeSlide reaches cardCount (first clone position),
  // after the CSS transition finishes we snap back to 0 with no transition.
  const cardCount = testimonials.length;
  const slideWidth = 100 / perPage;  // each card = 50% on desktop, 100% mobile

  const startAutoplay = useCallback(() => {
    if (autoplayRef.current) clearInterval(autoplayRef.current);
    autoplayRef.current = setInterval(() => {
      if (lockRef.current) return;
      setActiveSlide((prev) => prev + 1);
    }, 10000);
  }, []);

  const stopAutoplay = useCallback(() => {
    if (autoplayRef.current) {
      clearInterval(autoplayRef.current);
      autoplayRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (cardCount > perPage) startAutoplay();
    return stopAutoplay;
  }, [cardCount, perPage, startAutoplay, stopAutoplay]);

  // When the track finishes transitioning into the clone zone, snap back.
  const handleTransitionEnd = useCallback(() => {
    if (activeSlide >= cardCount) {
      lockRef.current = true;
      setTransitionEnabled(false);
      setActiveSlide(0);
      // Re-enable transition on the next frame so the snap is invisible.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTransitionEnabled(true);
          lockRef.current = false;
        });
      });
    }
  }, [activeSlide, cardCount]);

  const nextSlide = () => {
    if (lockRef.current) return;
    setActiveSlide((prev) => prev + 1);
    startAutoplay();
  };

  const prevSlide = () => {
    if (lockRef.current) return;
    setActiveSlide((prev) => (prev === 0 ? cardCount - 1 : prev - 1));
    startAutoplay();
  };

  const goToSlide = (idx) => {
    if (lockRef.current) return;
    setActiveSlide(idx);
    startAutoplay();
  };

  // Map activeSlide to a dot index (clones map to dot 0)
  const dotIndex = activeSlide >= cardCount ? 0 : activeSlide;

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="landing">

      {/* ── Hero ─────────────────────────────────────────────────── */}
      <header className="landing__hero">
        <nav className="landing__nav">
          <span className="landing__nav-brand">Deep Roots Milling</span>
          <Link to="/login" className="landing__nav-signin">Sign In</Link>
        </nav>
        <div className="landing__hero-video-wrap">
          <iframe
            src="https://www.youtube.com/embed/UB43h0p63YM?autoplay=1&mute=1&loop=1&playlist=UB43h0p63YM&controls=0&rel=0&playsinline=1&showinfo=0&modestbranding=1"
            title="Deep Roots Milling background"
            allow="autoplay; encrypted-media"
            allowFullScreen={false}
          />
        </div>
        <div className="landing__hero-inner">
          <p className="landing__wordmark">Deep Roots Milling</p>
          <div className="landing__divider">
            <span className="landing__divider-line" />
            <span className="landing__divider-ornament">❧</span>
            <span className="landing__divider-line" />
          </div>
          <h1 className="landing__title">
            Stone-Ground Flour, Milled Fresh for Your Business
          </h1>
          <p className="landing__subtitle">
            Heritage grains, water-powered millstones, 230 years of tradition.
            Wholesale flour for bakeries, restaurants, and retailers — never a commodity product.
          </p>
          <div className="landing__cta-group">
            <Link to="/signup" className="landing__cta-primary">
              Open a Wholesale Account
            </Link>
            <Link to="/catalog" className="landing__cta-secondary">
              View Products
            </Link>
          </div>
          <p className="landing__signin-prompt">
            Already have an account? <Link to="/login" className="landing__signin-link">Sign in</Link>
          </p>
        </div>
      </header>

      {/* ── Value Props ──────────────────────────────────────────── */}
      <section className="landing__values">
        <div className="landing__values-inner">
          <p className="landing__section-label">Why Deep Roots</p>
          <div className="landing__divider landing__divider--light">
            <span className="landing__divider-line" />
            <span className="landing__divider-ornament">❧</span>
            <span className="landing__divider-line" />
          </div>
          <div className="landing__values-grid">
            {VALUE_PROPS.map((prop) => (
              <div key={prop.title} className="landing__value-card">
                <span className="landing__value-icon">{prop.icon}</span>
                <h3 className="landing__value-title">{prop.title}</h3>
                <p className="landing__value-body">{prop.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Milling Schedule ─────────────────────────────────────── */}
      <section className="landing__schedule">
        <div className="landing__schedule-inner">
          <p className="landing__section-label">Milling Schedule</p>
          <div className="landing__divider landing__divider--dark">
            <span className="landing__divider-line" />
            <span className="landing__divider-ornament">❧</span>
            <span className="landing__divider-line" />
          </div>
          <p className="landing__schedule-intro">
            We mill in monthly sessions. Place your order before milling begins
            and we'll have it ready by the delivery date.
          </p>

          {loading ? (
            <div className="landing__loading">
              <div className="landing__loading-spinner" />
            </div>
          ) : sessions.length > 0 ? (
            <div className="landing__schedule-table-wrap">
              <table className="landing__schedule-table">
                <thead>
                  <tr>
                    <th>Session</th>
                    <th>Order By</th>
                    <th>Milling Dates</th>
                    <th>Delivery Starts</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((s) => (
                    <tr key={s.id}>
                      <td className="landing__schedule-label" data-label="Session">{s.label}</td>
                      <td data-label="Order By">{formatDate(s.mill_start)}</td>
                      <td data-label="Milling Dates">{formatDate(s.mill_start)} – {formatDate(s.mill_end)}</td>
                      <td data-label="Delivery Starts">{formatDate(s.delivery_start)}</td>
                      <td data-label="Status">
                        <span className={`landing__status landing__status--${s.status}`}>
                          {s.status === 'open' ? 'Accepting Orders' : 'Closed'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="landing__schedule-empty">
              No milling sessions scheduled at this time.
            </p>
          )}
        </div>
      </section>

      {/* ── Testimonials ─────────────────────────────────────────── */}
      {testimonials.length > 0 && (
        <section className="landing__testimonials">
          <div className="landing__testimonials-inner">
            <p className="landing__section-label landing__section-label--light">
              What Our Partners Say
            </p>
            <div className="landing__divider">
              <span className="landing__divider-line" />
              <span className="landing__divider-ornament">❧</span>
              <span className="landing__divider-line" />
            </div>

            <div
              className="landing__carousel"
              onMouseEnter={stopAutoplay}
              onMouseLeave={startAutoplay}
            >
              {cardCount > perPage && (
                <button
                  className="landing__carousel-arrow landing__carousel-arrow--prev"
                  onClick={prevSlide}
                  aria-label="Previous testimonial"
                >
                  ‹
                </button>
              )}

              <div className="landing__carousel-viewport">
                <div
                  ref={trackRef}
                  className={`landing__carousel-track${transitionEnabled ? '' : ' landing__carousel-track--no-transition'}`}
                  style={{ transform: `translateX(-${activeSlide * slideWidth}%)` }}
                  onTransitionEnd={handleTransitionEnd}
                >
                  {/* Real cards */}
                  {testimonials.map((t) => (
                    <div key={t.id} className="landing__carousel-slide">
                      <QuoteCard t={t} />
                    </div>
                  ))}
                  {/* Clone first `perPage` cards for seamless wrap */}
                  {cardCount > perPage &&
                    testimonials.slice(0, perPage).map((t) => (
                      <div key={`clone-${t.id}`} className="landing__carousel-slide" aria-hidden="true">
                        <QuoteCard t={t} />
                      </div>
                    ))
                  }
                </div>
              </div>

              {cardCount > perPage && (
                <button
                  className="landing__carousel-arrow landing__carousel-arrow--next"
                  onClick={nextSlide}
                  aria-label="Next testimonial"
                >
                  ›
                </button>
              )}
            </div>

            {cardCount > perPage && (
              <div className="landing__carousel-dots">
                {Array.from({ length: cardCount }, (_, idx) => (
                  <button
                    key={idx}
                    className={`landing__carousel-dot ${
                      idx === dotIndex ? 'landing__carousel-dot--active' : ''
                    }`}
                    onClick={() => goToSlide(idx)}
                    aria-label={`Go to card ${idx + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* ── Shipping & Delivery ──────────────────────────────────── */}
      <section className="landing__shipping">
        <div className="landing__shipping-inner">
          <p className="landing__section-label">Shipping &amp; Delivery</p>
          <div className="landing__divider landing__divider--light">
            <span className="landing__divider-line" />
            <span className="landing__divider-ornament">❧</span>
            <span className="landing__divider-line" />
          </div>
          <div className="landing__shipping-grid">
            {SHIPPING_ITEMS.map((item) => (
              <div key={item.label} className="landing__shipping-card">
                <h3 className="landing__shipping-title">{item.label}</h3>
                <p className="landing__shipping-detail">{item.detail}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Footer CTA ───────────────────────────────────────────── */}
      <footer className="landing__footer">
        <div className="landing__footer-inner">
          <h2 className="landing__footer-heading">
            Ready to serve stone-ground flour?
          </h2>
          <p className="landing__footer-sub">
            Apply for a wholesale account and get access to pricing,
            online ordering, and real-time fulfillment tracking.
          </p>
          <Link to="/signup" className="landing__cta-primary">
            Open a Wholesale Account
          </Link>
          <p className="landing__signin-prompt">
            Already have an account? <Link to="/login" className="landing__signin-link">Sign in</Link>
          </p>
        </div>
      </footer>

    </div>
  );
};

export default LandingPage;
